import { MoveLeft, MoveRight } from "lucide-react";
import { useState, useRef } from "react";

interface ImageTitleWithDescriptionProps {
  id: string;
  image: string;
  title: string;
  excerpt: string;
  readMore?: string[] | string;
}

interface Props {
  data: ImageTitleWithDescriptionProps[];
  isBlog?: boolean;
  handleKnowMore?: (id: string) => void;
}

export default function ImageTitleWithDescription({ data, isBlog, handleKnowMore }: Props) {
  const [currentPage, setCurrentPage] = useState(0);
  const carouselRef = useRef<HTMLDivElement>(null);
  const itemsPerPage = 3;
  const totalPages = Math.ceil(data.length / itemsPerPage);

  const scrollToTop = () => {
    if (carouselRef.current) {
      carouselRef.current.scrollIntoView({
        behavior: "smooth",
        block: "start",
      });
    }
  };

  const nextPage = (e: React.MouseEvent) => {
    e.preventDefault();
    setCurrentPage((prev) => (prev + 1) % totalPages);
    scrollToTop();
  };

  const prevPage = (e: React.MouseEvent) => {
    e.preventDefault();
    setCurrentPage((prev) => (prev - 1 + totalPages) % totalPages);
    scrollToTop();
  };

  const goToPage = (page: number, e: React.MouseEvent) => {
    e.preventDefault();
    setCurrentPage(page);
    scrollToTop();
  };

  const currentData = data.slice(
    currentPage * itemsPerPage,
    (currentPage + 1) * itemsPerPage
  );

  if (data.length <= 3) {
    return (
      <div className="space-y-16">
        {data.map((post: ImageTitleWithDescriptionProps, index: number) => (
          <div
            key={index}
            className="grid md:grid-cols-2 items-center gap-16 backdrop-blur-sm"
          >
            <div className="rounded-xl relative">
              <div className="relative overflow-hidden rounded-xl">
                <img
                  src={post.image}
                  alt={post.title}
                  className="w-full min-h-[420px] max-h-[420px] object-cover hover:scale-105 transition-transform duration-300"
                />
               {isBlog ? <div className="absolute inset-0 bg-black bg-opacity-30 pointer-events-none"></div> : null}
              </div>
              {isBlog ? (
                <div className="absolute bottom-0 left-0 z-10 p-8 w-full">
                  <h3 className="text-2xl md:text-4xl font-bold text-white leading-tight">
                    {post.title}
                  </h3>
                </div>
              ) : null}
            </div>
            <div className="text-white space-y-4 text-xl leading-relaxed">
              {isBlog ? null : (
                <h3 className="text-3xl sm:text-5xl text-white leading-tight">
                  {post.title}
                </h3>
              )}
              <p>{post.excerpt}</p>
              {Array.isArray(post.readMore) ? (
                post.readMore.map((item: string, idx: number) => (
                  <p key={idx}>{item}</p>
                ))
              ) : (
                <p>{post.readMore}</p>
              )}
              <div className="flex justify-end">
                <button 
                  className="text-[#e7dbdb]/55 hover:text-white transition-colors duration-200"
                  onClick={() => handleKnowMore?.(post.id)}
                >
                  Know More {">>"}
                </button>
              </div>
            </div>
          </div>
        ))}
      </div>
    );
  }

  return (
    <div className="space-y-8">
      {/* Carousel Content */}
      <div className="space-y-8" ref={carouselRef}>
        {currentData.map(
          (post: ImageTitleWithDescriptionProps, index: number) => (
            <div
              key={index}
              className="grid md:grid-cols-2 items-center gap-16 backdrop-blur-sm"
            >
              <div className="rounded-xl relative overflow-hidden">
                <img
                  src={post.image}
                  alt={post.title}
                  className="w-full min-h-96 object-cover"
                />
                {isBlog ? (
                  <div className="absolute bottom-0 left-0 z-10 p-8 w-full">
                    <h3 className="text-2xl md:text-4xl font-bold text-white leading-tight">
                      {post.title}
                    </h3>
                  </div>
                ) : null}
              </div>
              <div className="text-white space-y-4 text-xl leading-relaxed">
                {isBlog ? null : (
                  <h3 className="text-3xl sm:text-5xl text-white leading-tight">
                    {post.title}
                  </h3>
                )}
                <p>{post.excerpt}</p>
                {Array.isArray(post.readMore) ? (
                  post.readMore.map((item: string, idx: number) => (
                    <p key={idx}>{item}</p>
                  ))
                ) : (
                  <p>{post.readMore}</p>
                )}
                <div className="flex justify-end">
                  <button 
                    className="text-[#e7dbdb]/55 hover:text-white transition-colors duration-200"
                    onClick={() => handleKnowMore?.(post.id)}
                  >
                    Know More {">>"}
                  </button>
                </div>
              </div>
            </div>
          )
        )}
      </div>

      {/* Carousel Navigation */}
      <div className="flex items-center justify-center space-x-4 mt-12">
        {/* Previous Button */}
        <button
          onClick={(e) => prevPage(e)}
          className="transform z-10 text-white/60 hover:text-white transition-colors duration-200 bg-white/10 rounded-full p-1"
        >
          <MoveLeft />
        </button>

        {/* Page Indicators */}
        <div className="flex space-x-3">
          {Array.from({ length: totalPages }, (_, index) => (
            <button
              key={index}
              onClick={(e) => goToPage(index, e)}
              className={`w-14 h-1 rounded-xl transition-all duration-300 ${
                index === currentPage
                  ? "bg-white scale-125"
                  : "bg-white/30 hover:bg-white/50"
              }`}
              aria-label={`Go to page ${index + 1}`}
            />
          ))}
        </div>

        {/* Next Button */}
        <button
          onClick={(e) => nextPage(e)}
          className="transform z-10 text-white/60 hover:text-white transition-colors duration-200 bg-white/10 rounded-full p-1"
        >
          <MoveRight />
        </button>
      </div>

      {/* Page Info */}
      {/* <div className="text-center text-white/70 text-sm">
        Page {currentPage + 1} of {totalPages} • Showing {currentData.length} of {data.length} items
      </div> */}
    </div>
  );
}
